import {getQuery, postQuery} from "../../common/utils";
import axios from "axios";
import {httpGatewayService} from "@/modules/common/services";
import storageService from "@/modules/common/services/storageService";
import {isPersistentCacheEnabled} from "@/modules/common/utils/persistentCache";

const V3_INSTALLS_ROUTE = 'v3/apps/business_app_installs';
const V3_APPS_ROUTE = 'v3/apps/apps';

const APPS_TTL_MS = 15 * 60 * 1000; // 15m
const appsCacheKey = (businessUid) => `APP_MARKET_APPS_V3_${businessUid}`;

const getExtensionValue = (extensions, type) => {
  const ext = extensions?.find(e => e.type === type);
  return ext?.value ?? null;
};

const mapV3AppToLegacy = (v3App) => {
  const extensions = v3App.extensions || [];
  return {
    app_id: v3App.uid,
    app_name: v3App.name,
    app_code_name: v3App.app_code_name,
    app_host: v3App.app_host,
    api_uri: v3App.api_uri ?? v3App.internal?.api_uri,
    app_types: v3App.app_types || [],
    description: v3App.app_market?.description || {},
    app_screenshot_uris: v3App.app_market?.app_screenshot_uris || [],
    app_mobile_screenshot_uris: v3App.app_market?.app_mobile_screenshot_uris || [],
    app_features: v3App.app_market?.app_features || [],
    supported_countries: v3App.app_market?.supported_countries || [],
    locales: v3App.app_market?.locales || [],
    privacy_policy_link: v3App.app_market?.privacy_policy_link,
    contact_support_link: v3App.app_market?.contact_support_link,
    personal_connection: v3App.app_market?.personal_connection,
    url_params: v3App.url_params || [],
    permissions: v3App.permissions || [],
    open_in_new_tab: v3App.open_in_new_tab || false,
    supported_in_mobile: v3App.supported_in_mobile,
    status: v3App.assignment_types?.includes('installed') ? 'installed' : 'not_installed',
    requires_upgrade: v3App.app_market?.requires_upgrade || false,
    requires_upgrade_if_paid: v3App.app_market?.requires_upgrade_if_paid || false,
    assignment_types: v3App.assignment_types || [],
    has_offering: v3App.assignment_types?.includes('directory_offering') || false,
    old_integration_name: getExtensionValue(extensions, 'old_integration_name'),
    deep_link: v3App.internal?.deep_link,
    full_screen: v3App.internal?.full_screen || false,
    additional_params: getExtensionValue(extensions, 'additional_params'),
  };
};

const getAppsV3 = async ({businessUid, force = false} = {}) => {
  const cacheKey = (isPersistentCacheEnabled() && businessUid) ? appsCacheKey(businessUid) : null;

  if (!force && cacheKey) {
    const cached = storageService.getWithExpiry(cacheKey);
    if (cached) return cached;
  }

  try {
    const res = await httpGatewayService.get(
      `${V3_APPS_ROUTE}?per_page=100&assignment_type[]=directory_offering&assignment_type[]=installed`
    );
    const v3Apps = res?.data?.apps || [];
    const apps = v3Apps
      .filter(app => !app.app_types?.includes('billing_app') && !app.assignment_types?.includes('internal'))
      .map(mapV3AppToLegacy);

    if (cacheKey) {
      storageService.saveWithExpiry(cacheKey, apps, APPS_TTL_MS);
    }

    return apps;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const clearAppsCache = (businessUid) => {
  if (businessUid) storageService.remove(appsCacheKey(businessUid));
};

const getAppsByType = async (params) => {
  let result;
  try {
    result = await getQuery(`settings/get_available_apps_by_type?type=${params.appType}&buid=${params.businessUid}`)
    result = result.data;
  } catch (error) {
    result = {code: 'error'};
    console.error(error)
  }

  return result;
}

const getExternalApps = async () => {

  let result;
  try {
    result = await getQuery('settings/get_external_applications')
    result = result.data;
  } catch (error) {
    result = {code: 'error'};
    console.error(error)
  }

  return result;
}


const installApp = async (params) => {
  let result;
  try {
    const res = await httpGatewayService.post(V3_INSTALLS_ROUTE, {
      business_uid: params.business_uid,
      app_code_name: params.app_code_name,
    });
    result = res.data;
  } catch (error) {
    result = {code: 'error'};
    console.error(error)
  }
  return result;
}

const uninstallApp = async (params) => {
  let result;
  try {
    const res = await httpGatewayService.get(V3_INSTALLS_ROUTE, {
      business_uid: params.business_uid,
      app_code_name: params.app_code_name,
    });
    const installs = res?.data?.business_app_installs || [];
    const install = installs[0];

    if (install) {
      await httpGatewayService.remove(`${V3_INSTALLS_ROUTE}/${install.uid}`);
    }

    result = {app_code_name: params.app_code_name};
  } catch (error) {
    result = {code: 'error'};
    console.error(error);
  }
  return result;
}

const logUpgradeButtonClicked = async (app_code_name) => {
  try {
    await postQuery('platform/v1/apps/manager/upgrade_clicked_log', {app_code_name: app_code_name})
  } catch (error) {
    console.error(error)
  }
}

const getCalendarSyncAppPermissions = async (staffUid) => {
  let result;
  try {
    result = await getQuery(`business/scheduling/v1/calendar_syncs?staff_uid=${staffUid}`, {new_api: true})
    result = result.data;
  } catch (error) {
    result = {code: 'error'};
    console.error(error)
  }

  return result;
}

const getSyncAccount = async (app_uri, staff_uid) => {
  try {
    const res = await axios.get(`${app_uri}/accounts?staff_uid=${staff_uid}`)
    return res.data.data.account
  } catch (error) {
    if (error?.response?.status === 422 && error?.response?.data?.errors[0]?.message === 'invalid_grant') {
      return {error: 'invalid_grant'}
    }
  }
}

const appsWithBetaBadge = ['esignatures', 'tips'];

export const appsService = {
  getAppsV3,
  clearAppsCache,
  getAppsByType,
  getExternalApps,
  installApp,
  uninstallApp,
  logUpgradeButtonClicked,
  getCalendarSyncAppPermissions,
  getSyncAccount,
  appsWithBetaBadge
};
